/**
 * Shared API Service Patterns
 *
 * Platform-agnostic API helpers that work with any HTTP client (Axios, fetch, etc.)
 * Provides: request building, pagination, response parsing, tenant context,
 *           error classification, and a CRUD entity-service factory.
 *
 * Consumers:
 *   frontend/  — businessApi.ts / workformsApi.ts delegate to createEntityService
 *   mobile/    — ApiService.ts delegates to createEntityService + shared helpers
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface ListParams {
  page?: number;
  page_size?: number;
  search?: string;
  ordering?: string;
  [key: string]: string | number | boolean | undefined;
}

export interface ApiEndpoints {
  customers: (tenantId: string) => string;
  suppliers: (tenantId: string) => string;
  inquiries: (tenantId: string) => string;
  purchaseOrders: (tenantId: string) => string;
  salesOrders: (tenantId: string) => string;
  plants: (tenantId: string) => string;
  carriers: (tenantId: string) => string;
  contacts: (tenantId: string) => string;
  invoices: (tenantId: string) => string;
  workflows: (tenantId: string) => string;
}

// ─── Endpoint Registry (Single Source of Truth) ──────────────────────────────

export const endpoints: ApiEndpoints = {
  customers: (tenantId) => `/tenants/${tenantId}/customers/`,
  suppliers: (tenantId) => `/tenants/${tenantId}/suppliers/`,
  inquiries: (tenantId) => `/tenants/${tenantId}/inquiries/`,
  purchaseOrders: (tenantId) => `/tenants/${tenantId}/purchase-orders/`,
  salesOrders: (tenantId) => `/tenants/${tenantId}/sales-orders/`,
  plants: (tenantId) => `/tenants/${tenantId}/plants/`,
  carriers: (tenantId) => `/tenants/${tenantId}/carriers/`,
  contacts: (tenantId) => `/tenants/${tenantId}/contacts/`,
  invoices: (tenantId) => `/tenants/${tenantId}/invoices/`,
  workflows: (tenantId) => `/tenants/${tenantId}/workflows/`,
};

// ─── Query Parameter Builder ─────────────────────────────────────────────────

export function buildQueryString(params: ListParams): string {
  const filtered = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);

  return filtered.length > 0 ? `?${filtered.join('&')}` : '';
}

// ─── Pagination Helpers ──────────────────────────────────────────────────────

export function getNextPage(response: PaginatedResponse<unknown>): number | null {
  if (!response.next) return null;
  try {
    const url = new URL(response.next);
    const page = url.searchParams.get('page');
    return page ? parseInt(page, 10) : null;
  } catch {
    return null;
  }
}

export function getTotalPages(count: number, pageSize: number): number {
  return Math.ceil(count / pageSize);
}

// ─── Response Parsing ────────────────────────────────────────────────────────

/**
 * Safely extract data from a paginated or direct API response.
 * Handles both { results: T[] } and direct T[] responses.
 */
export function extractResults<T>(response: unknown): T[] {
  if (Array.isArray(response)) return response;
  if (response && typeof response === 'object' && 'results' in response) {
    return (response as PaginatedResponse<T>).results;
  }
  return [];
}

// ─── Tenant Context ──────────────────────────────────────────────────────────

/**
 * Build a full API URL with tenant context.
 */
export function tenantUrl(baseUrl: string, tenantId: string, path: string, params?: ListParams): string {
  const fullPath = path.startsWith('/') ? path : `/${path}`;
  const query = params ? buildQueryString(params) : '';
  return `${baseUrl}/tenants/${tenantId}${fullPath}${query}`;
}

// ─── Request Deduplication ───────────────────────────────────────────────────

const pendingRequests = new Map<string, Promise<unknown>>();

/**
 * Deduplicate identical concurrent GET requests.
 * If the same URL is already in-flight, return the existing promise.
 */
export function deduplicateRequest<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
  const existing = pendingRequests.get(key);
  if (existing) return existing as Promise<T>;

  const promise = requestFn().finally(() => {
    pendingRequests.delete(key);
  });

  pendingRequests.set(key, promise);
  return promise;
}

// ─── Response Normalization ──────────────────────────────────────────────────

type PaginatedLike<T> = {
  count?: number;
  results: T[];
  next?: string | null;
  previous?: string | null;
};

function isPaginatedLike<T>(value: unknown): value is PaginatedLike<T> {
  return (
    !!value &&
    typeof value === 'object' &&
    Array.isArray((value as PaginatedLike<T>).results)
  );
}

/**
 * Normalize a raw API payload (array or paginated wrapper) into a
 * canonical PaginatedResponse<T>.
 *
 * Handles:
 *   - Direct arrays:  T[] → { count: n, results: T[], next: null, previous: null }
 *   - DRF pagination: { count, results, next, previous }
 *   - Anything else:  empty response
 */
export function normalizePaginated<T>(payload: unknown): PaginatedResponse<T> {
  if (Array.isArray(payload)) {
    return { count: payload.length, results: payload as T[], next: null, previous: null };
  }

  if (isPaginatedLike<T>(payload)) {
    return {
      count: typeof payload.count === 'number' ? payload.count : payload.results.length,
      results: payload.results,
      next: payload.next ?? null,
      previous: payload.previous ?? null,
    };
  }

  return { count: 0, results: [], next: null, previous: null };
}

// ─── Error Classification ────────────────────────────────────────────────────

type ErrorWithStatus = { response?: { status?: number } };

function getResponseStatus(error: unknown): number | undefined {
  return (error as ErrorWithStatus)?.response?.status;
}

/** Returns true when the error is an HTTP 404 Not Found. */
export function isNotFoundError(error: unknown): boolean {
  return getResponseStatus(error) === 404;
}

/** Returns true when the error is an HTTP 5xx Server Error. */
export function isServerError(error: unknown): boolean {
  const status = getResponseStatus(error);
  return typeof status === 'number' && status >= 500;
}

// ─── Entity Service Abstractions ─────────────────────────────────────────────

/**
 * Minimal HTTP client contract.
 * Satisfied by both the frontend Axios wrapper (businessApi/workformsApi)
 * and the mobile ApiServiceClass (ApiService.ts).
 */
export interface EntityHttpClient {
  get<T>(path: string, params?: Record<string, unknown>): Promise<T>;
  post<T>(path: string, data?: unknown): Promise<T>;
  patch<T>(path: string, data?: unknown): Promise<T>;
  put<T>(path: string, data?: unknown): Promise<T>;
  delete(path: string): Promise<void>;
}

/**
 * Standard CRUD service shape returned by createEntityService.
 * Both frontend and mobile entity modules conform to this interface.
 */
export interface EntityService<T> {
  list(params?: Record<string, unknown>): Promise<PaginatedResponse<T>>;
  get(id: number | string): Promise<T>;
  create(data: Partial<T>): Promise<T>;
  update(id: number | string, data: Partial<T>): Promise<T>;
  patch(id: number | string, data: Partial<T>): Promise<T>;
  delete(id: number | string): Promise<void>;
}

/**
 * Factory that generates a fully-typed CRUD service for a REST resource.
 *
 * @param client   Any EntityHttpClient (Axios adapter, ApiServiceClass, etc.)
 * @param basePath Resource path segment, e.g. "customers" or "/customers/"
 *
 * @example
 *   // Frontend (businessApi.ts)
 *   export const suppliersApi = createEntityService<Supplier>(businessHttpClient, 'suppliers');
 *
 *   // Mobile (ApiService.ts)
 *   const customersService = createEntityService<Customer>(ApiService, 'customers');
 */
export function createEntityService<T>(
  client: EntityHttpClient,
  basePath: string
): EntityService<T> {
  const base = `/${basePath.replace(/^\/|\/$/g, '')}/`;
  return {
    list: (params) =>
      client.get<unknown>(base, params).then((r) => normalizePaginated<T>(r)),
    get: (id) => client.get<T>(`${base}${id}/`),
    create: (data) => client.post<T>(base, data),
    update: (id, data) => client.put<T>(`${base}${id}/`, data),
    patch: (id, data) => client.patch<T>(`${base}${id}/`, data),
    delete: (id) => client.delete(`${base}${id}/`),
  };
}

export * from './baseApiClient';
export * from './jwtService';

// ─── Tenant-Scoped Fallback List ─────────────────────────────────────────────

/**
 * Fetch a tenant-scoped list with automatic path fallback.
 *
 * Tries `nestedPath` first (e.g. `/tenants/{id}/customers/`).
 * If it returns 404, retries with `fallbackPath` (e.g. `/customers/`).
 * All other errors are re-thrown to the caller.
 *
 * Extracted from `mobile/src/services/ApiService.impl.ts` (issue #6628 Phase 2).
 * Usable by any `EntityHttpClient` — Axios wrapper or native fetch adapter.
 *
 * @example
 *   const data = await getTenantScopedFallbackList<Customer>(
 *     client,
 *     `/tenants/${tenantId}/customers/`,
 *     '/customers/',
 *     { search: query }
 *   );
 */
export async function getTenantScopedFallbackList<T>(
  client: EntityHttpClient,
  nestedPath: string,
  fallbackPath: string,
  params?: ListParams & Record<string, unknown>
): Promise<PaginatedResponse<T>> {
  try {
    const response = await client.get<unknown>(nestedPath, params);
    return normalizePaginated<T>(response);
  } catch (error: unknown) {
    if (!isNotFoundError(error)) throw error;

    const fallback = await client.get<unknown>(fallbackPath, params);
    return normalizePaginated<T>(fallback);
  }
}
